import { cameraErrorMessages } from "@studystraight/shared-config";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  CameraError,
  type CameraErrorKind,
  type CameraFacing,
  createCameraService,
} from "../platform/camera/CameraService.js";

export interface UseCameraOptions {
  /** Camera trước/sau đã lưu ở settings; chỉ đọc một lần lúc mount (xem `initialFacingRef`). */
  facingMode?: CameraFacing;
  /** Gọi khi người dùng bấm đổi camera, để App ghi lựa chọn xuống store (§27). */
  onFacingModeChange?: (facing: CameraFacing) => void;
}

export interface UseCameraState {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  status: "idle" | "starting" | "live" | "error";
  errorKind: CameraErrorKind | null;
  errorMessage: string | null;
  facingMode: CameraFacing;
  stream: MediaStream | null;
  start: () => void;
  switchCamera: () => void;
  stop: () => void;
}

/**
 * Gắn `MediaStream` vào một `<video>` và quản lý lifecycle.
 *
 * Không tự start khi mount: §7 yêu cầu người dùng chủ động bắt đầu, và trên iOS gọi
 * `getUserMedia` ngoài một user gesture rất dễ bị chặn.
 */
export function useCamera(options: UseCameraOptions = {}): UseCameraState {
  // Ref, không phải dependency: settings được lưu xuống store nên object `options` mới xuất hiện
  // sau mỗi lần đổi camera. Nếu `service` dựng lại theo nó thì stream đang chạy bị stop ngay sau
  // khi vừa mở — và trên iOS xin lại `getUserMedia` ngoài user gesture rất dễ bị chặn.
  const initialFacingRef = useRef(options.facingMode ?? "user");
  const onFacingModeChangeRef = useRef(options.onFacingModeChange);
  onFacingModeChangeRef.current = options.onFacingModeChange;

  const service = useMemo(() => createCameraService(initialFacingRef.current), []);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState<UseCameraState["status"]>("idle");
  const [errorKind, setErrorKind] = useState<CameraErrorKind | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<CameraFacing>(initialFacingRef.current);

  const attach = useCallback((next: MediaStream) => {
    setStream(next);
    const video = videoRef.current;
    if (video) {
      video.srcObject = next;
      // play() có thể bị reject nếu tab ẩn — không phải lỗi cần báo cho người dùng.
      void video.play().catch(() => undefined);
    }
  }, []);

  const run = useCallback(
    (action: () => Promise<MediaStream>) => {
      setStatus("starting");
      setErrorKind(null);
      action()
        .then((next) => {
          attach(next);
          setFacingMode(service.facingMode);
          setStatus("live");
        })
        .catch((err: unknown) => {
          setErrorKind(err instanceof CameraError ? err.kind : "UNKNOWN");
          setStatus("error");
        });
    },
    [attach, service],
  );

  // Đọc facingMode từ service, không từ state: nhờ vậy `start` có identity ổn định và effect
  // "mở camera khi vào screen" không bị chạy lại mỗi lần người dùng đổi camera trước/sau.
  const start = useCallback(() => {
    run(() => service.start({ facingMode: service.facingMode }));
  }, [run, service]);

  // Chỉ `switchCamera` báo ngược lên App, không phải mọi lần `start()`: start dùng đúng giá trị
  // vừa đọc từ settings, ghi lại nó là một lần write IndexedDB không đổi gì — nhân ba vì ba screen
  // đều mở camera.
  const switchCamera = useCallback(() => {
    run(async () => {
      const next = await service.switchCamera();
      // Báo SAU khi stream mở thành công: máy không có camera sau thì `switchCamera` throw, và
      // lưu lại "environment" lúc đó sẽ làm mọi lần mở app tiếp theo hỏng camera ngay từ đầu.
      onFacingModeChangeRef.current?.(service.facingMode);
      return next;
    });
  }, [run, service]);

  const stop = useCallback(() => {
    service.stop();
    setStream(null);
    setStatus("idle");
  }, [service]);

  useEffect(() => {
    const off = service.onTrackEnded(() => {
      // Không giữ frame cuối trên màn hình: người dùng phải thấy rõ camera đã dừng (quyết định #14).
      setStream(null);
      setStatus("error");
      setErrorKind("UNKNOWN");
    });
    return () => {
      off();
      service.stop();
    };
  }, [service]);

  return {
    videoRef,
    status,
    errorKind,
    errorMessage: errorKind ? cameraErrorMessages[errorKind] : null,
    facingMode,
    stream,
    start,
    switchCamera,
    stop,
  };
}
